import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import styles from './admin.module.css';

export interface EmailDeliveryRecipientProps {
  /** Alıcı özetinin ilk 8 karakteri. Ham sha256 sunucudan HİÇ gelmez. */
  readonly hashPrefix: string;
  /** Kayıt bir hesaba bağlıysa kullanıcı ekranına köprü; yoksa `null`. */
  readonly userLink: string | null;
  /**
   * Bağlantı bileşeni DIŞARIDAN gelir — bileşen yönlendiriciye bağlanmaz ve
   * test onu düz `<a>` ile sürebilir.
   */
  readonly LinkComponent: ComponentType<{ to: string; className?: string; children: ReactNode }>;
}

/**
 * ALICI HÜCRESİ — ADRES YOK, ÖZET VAR.
 *
 * Kırmızı çizgi burada görünür hâle gelir: gönderim kaydı adresi saklamaz, bu
 * yüzden panel de adres gösteremez. Personelin elindeki tek eşleştirme aracı
 * özetin ilk 8 karakteridir ve kopyalanabilir olması destek talebiyle satırı
 * eşleştirmeye yeter.
 *
 * KOPYALAMA FAIL-OPEN: pano API'si yoksa ya da izin verilmezse hücre sessizce
 * eski hâlinde kalır — kolaylık için eklenen bir yan etki ana işi (listeyi
 * okumak) düşüremez.
 */
export function EmailDeliveryRecipient({
  hashPrefix,
  userLink,
  LinkComponent,
}: EmailDeliveryRecipientProps) {
  const t = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    void navigator.clipboard
      ?.writeText(hashPrefix)
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  return (
    <span className={styles.recipientCell}>
      {/*
        ERİŞİLEBİLİR AD ÖZETİ TAŞIR (16.09.2026, tech-lead).
        Görünen metin `aria-hidden`dır — süslemeli üç nokta ekran okuyucuda
        gürültüdür. Ama etiket yalnız "Alıcı özetini kopyala" deseydi, EKRAN
        OKUYUCU KULLANAN PERSONEL her satırda AYNI cümleyi duyar ve satırları
        birbirinden ayıramazdı; hücrenin tek ayırt edici içeriği zaten özettir.
        Bu yüzden özet etikete GİRER, ekrandaki metin görsel kalır.
      */}
      <button
        type="button"
        className={styles.hashButton}
        onClick={copy}
        title={t.t('admin.emailDeliveries.recipientHint')}
        aria-label={t.t('admin.emailDeliveries.copyRecipient', { prefix: hashPrefix })}
      >
        <span aria-hidden="true">
          {t.t('admin.emailDeliveries.recipientShort', { prefix: hashPrefix })}
        </span>
      </button>
      {copied ? (
        <span className={styles.cellMeta} role="status">
          {t.t('admin.emailDeliveries.copied')}
        </span>
      ) : null}
      {userLink ? (
        <LinkComponent to={userLink} className={styles.recipientLink}>
          {t.t('admin.emailDeliveries.relatedUser')}
        </LinkComponent>
      ) : null}
    </span>
  );
}
